import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import PinInput from 'react-pin-input';

import { Typography } from 'src/typography';
import { SmallLoader } from 'shared/components';
import useTranslation from 'hooks/use-translation';

const CODE_LENGTH = 6;

/**
 * OTP error states. `expired` (TTL lapsed) and `max_attempts` (5 wrong guesses) are
 * distinct causes with distinct copy, but share the same remedy -- request a new code.
 */
export type OtpErrorKind = 'conflict' | 'expired' | 'generic' | 'invalid' | 'max_attempts' | 'throttled' | null;

export type OtpEntryProps = {
  /** Last 4 digits only. Never the full number. May be null/undefined/blank if unavailable. */
  phoneHint: string | null | undefined;
  onChangeNumberClick: () => void;
  onSubmit: (code: string) => void;
  onBack: () => void;
  onResend: () => void;
  isSubmitting?: boolean;
  resendSecondsLeft?: number;
  errorKind?: OtpErrorKind;
  description?: string;
  /**
   * Whether to render the `‹ Back` affordance. Defaults to `true` (the Nudge modal's own usage,
   * which always has a `homeStep` to return to). The My Account CTA's thin OTP host -- a
   * single-purpose task, not a step in a wizard -- passes `false`: there is no other screen to
   * go back to.
   */
  showBackLink?: boolean;
  /**
   * Whether to render the inline "Change number" link. Defaults to `true`. The My Account CTA's
   * host passes `false` -- the account form's own phone field is the canonical place to edit an
   * existing number, so this component should not offer a second write path to it.
   */
  showChangeNumberLink?: boolean;
};

/** `(***) *** NNNN` -- fixed placeholder mask around the real 4-digit hint. */
export function formatPhoneHintMask(phoneHint: string): string {
  return `(***) *** ${phoneHint}`;
}

/** True when there is no usable 4-digit hint to mask (null, undefined, or blank). */
function hasPhoneHint(phoneHint: string | null | undefined): phoneHint is string {
  return Boolean(phoneHint && phoneHint.trim().length > 0);
}

function getPinBoxBackgroundColor({ isSubmitting, hasError }: { isSubmitting: boolean; hasError: boolean }): string {
  if (isSubmitting) {
    return '#F3F6F8';
  }
  return hasError ? '#FFF5F5' : '#FFFFFF';
}

export const OtpEntry = ({
  phoneHint,
  onChangeNumberClick,
  onSubmit,
  onBack,
  onResend,
  isSubmitting = false,
  resendSecondsLeft = 0,
  errorKind = null,
  description,
  showBackLink = true,
  showChangeNumberLink = true,
}: OtpEntryProps): JSX.Element => {
  const { t } = useTranslation();
  const pinInputRef = useRef<PinInput | null>(null);

  // Clear the pin input whenever a new (non-conflict, non-generic) error comes in so the
  // user can re-enter -- `code_invalid` should not leave stale/wrong digits behind. Deliberate
  // for `max_attempts` too, not inherited by accident -- the old code is dead either way.
  useEffect(() => {
    if (errorKind === 'invalid' || errorKind === 'expired' || errorKind === 'max_attempts') {
      pinInputRef.current?.clear();
    }
  }, [errorKind]);

  // Remounting the whole `PinInput` (via `resendKey`) is the trigger for restoring focus after a
  // resend, not `errorKind` -- the inline link and the bottom button both call the same
  // `onResend`, and `errorKind` going truthy -> null doesn't distinguish "a resend just
  // completed" from any other error-clearing transition. `resendInFlightRef` is set by
  // `handleResendClick` (below) so this only fires for an actual resend, not e.g. a failed OTP
  // submit's own isSubmitting flip.
  //
  // A plain `pinInputRef.current?.focus()` on that transition was tried and doesn't work: the
  // parent clears `errorKind` and sets `isSubmitting`/disables the inputs in the same batch, so
  // by the time this effect's *own* commit lands, the pin boxes are still disabled and `.focus()`
  // on a disabled `<input>` is a silent no-op. Waiting for `isSubmitting` to flip back to `false`
  // (inputs re-enabled) fixes that, but a stale `react-pin-input` `PinItem` also never receives a
  // real `blur` event when its input is disabled while focused (the browser just drops focus),
  // so its internal `state.focus` -- and therefore its focus-ring style -- stays stuck "on".
  // Remounting via `key` discards that stale instance/state entirely and lets
  // `PinInput.componentDidMount`'s own `focus` prop handling put real focus (and only real
  // focus) on box 0, rather than patching around the stale internal state from outside.
  const resendInFlightRef = useRef(false);
  const wasSubmittingRef = useRef(isSubmitting);
  const [resendKey, setResendKey] = useState(0);

  useEffect(() => {
    const wasSubmitting = wasSubmittingRef.current;
    wasSubmittingRef.current = isSubmitting;
    if (wasSubmitting && !isSubmitting && resendInFlightRef.current) {
      resendInFlightRef.current = false;
      setResendKey((key) => key + 1);
    }
  }, [isSubmitting]);

  const handleResendClick = (): void => {
    resendInFlightRef.current = true;
    onResend();
  };

  const handleComplete = (code: string): void => {
    if (code.length === CODE_LENGTH && !isSubmitting) {
      onSubmit(code);
    }
  };

  const errorMessage = (() => {
    switch (errorKind) {
      case 'invalid':
        return t('phoneVerificationNudge.otpInvalid', "That code isn't right. Please try again.");
      case 'expired':
        return t('phoneVerificationNudge.otpExpired', 'This code has expired. Request a new one below.');
      case 'max_attempts':
        return t(
          'phoneVerificationNudge.otpMaxAttempts',
          "You've entered too many incorrect codes. Request a new code below."
        );
      case 'conflict':
        return t(
          'phoneVerificationNudge.otpConflict',
          'This phone number is already connected to another account. Try a different phone number.'
        );
      case 'throttled':
        return t(
          'phoneVerificationNudge.otpResendThrottled',
          "We can't send another code to this number right now. A code you already received may still work, or you can use a different number."
        );
      case 'generic':
        return t('phoneVerificationNudge.otpGenericError', 'Something went wrong on our end. Please try again.');
      case null:
      default:
        return null;
    }
  })();

  const offerResend = errorKind === 'expired' || errorKind === 'max_attempts';

  return (
    <Container data-testid='phone-verification-nudge-otp'>
      <Heading size='medium' weight='bold' tag='h2'>
        {t('phoneVerificationNudge.otpTitle', 'Enter the 6-digit code')}
      </Heading>
      {description && <Description>{description}</Description>}
      <SmsSentTo data-testid='phone-verification-nudge-otp-sms-sent-to'>
        {hasPhoneHint(phoneHint) ? (
          <>
            {t('phoneVerificationNudge.otpSmsSentTo', 'SMS Sent to:')} {formatPhoneHintMask(phoneHint)}
          </>
        ) : (
          t('phoneVerificationNudge.otpSmsSentToNoHint', 'SMS sent to your phone on file')
        )}
      </SmsSentTo>
      {showChangeNumberLink && errorKind !== 'throttled' && (
        <ChangeNumberLink type='button' onClick={onChangeNumberClick} disabled={isSubmitting}>
          {t('phoneVerificationNudge.changeNumberLink', 'Change number')}
        </ChangeNumberLink>
      )}

      <PinInputContainer>
        <StyledPinInput
          key={resendKey}
          length={CODE_LENGTH}
          focus
          type='numeric'
          inputMode='numeric'
          autoComplete='one-time-code'
          ref={pinInputRef}
          onComplete={handleComplete}
          disabled={isSubmitting}
          inputStyle={{
            borderRadius: '6px',
            fontSize: '20px',
            color: '#4f5d68',
            fontWeight: 'bold',
            height: '50px',
            width: '45px',
            marginLeft: '4px',
            marginRight: '4px',
            border: errorMessage ? '1px solid #E53935' : '1px solid #BCCAD2',
            backgroundColor: getPinBoxBackgroundColor({ isSubmitting, hasError: Boolean(errorMessage) }),
          }}
          inputFocusStyle={{
            border: errorMessage ? '1px solid #E53935' : '1px solid #4C94D7',
            boxShadow: errorMessage ? 'none' : '0px 1px 6px rgba(11, 153, 230, 0.4)',
          }}
        />
      </PinInputContainer>

      {errorMessage && <ErrorMessage data-testid='phone-verification-nudge-otp-error'>{errorMessage}</ErrorMessage>}

      {isSubmitting ? (
        <LoaderContainer data-testid='phone-verification-nudge-otp-spinner'>
          <SmallLoader height={24} color='#0b99e6' />
        </LoaderContainer>
      ) : (
        <ActionsRow hasBackLink={showBackLink}>
          {showBackLink && (
            <BackButton type='button' onClick={onBack} data-testid='phone-verification-nudge-otp-back'>
              {t('phoneVerificationNudge.back', '‹ Back')}
            </BackButton>
          )}
          {errorKind === 'throttled' ? (
            <ResendButton
              type='button'
              onClick={onChangeNumberClick}
              data-testid='phone-verification-nudge-otp-change-number-button'
            >
              {t('phoneVerificationNudge.changeNumberButton', 'Change number')}
            </ResendButton>
          ) : (
            // `offerResend` already renders a dedicated "Request a new code" CTA below --
            // don't also show this generic resend button, or there'd be two doing the same thing.
            !offerResend && (
              <ResendButton
                type='button'
                onClick={handleResendClick}
                disabled={resendSecondsLeft > 0}
                data-testid='phone-verification-nudge-otp-resend'
              >
                {t('phoneVerificationNudge.resendCode', 'Resend code')}
                {resendSecondsLeft > 0 ? ` (${resendSecondsLeft})` : ''}
              </ResendButton>
            )
          )}
        </ActionsRow>
      )}
      {offerResend && !isSubmitting && (
        <InlineResendLink
          type='button'
          onClick={handleResendClick}
          disabled={resendSecondsLeft > 0}
          data-testid='phone-verification-nudge-otp-inline-resend'
        >
          {resendSecondsLeft > 0
            ? `${t('phoneVerificationNudge.requestNewCodeCountdown', 'Request a new code in')} ${resendSecondsLeft}s`
            : t('phoneVerificationNudge.requestNewCode', 'Request a new code')}
        </InlineResendLink>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const Heading = styled(Typography.Heading)`
  align-self: flex-start;
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
  margin: 0;
  padding-right: 32px;
  text-align: left;
`;

const Description = styled.p`
  color: var(--dcm-modal-secondary-text-color, ${({ theme }) => theme.colors.grey[30]});
  font-size: 14px;
  margin: 0;
  text-align: center;
`;

const SmsSentTo = styled(Description)``;

const ChangeNumberLink = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: var(--dct--customized-colors-buttonsLinks, ${({ theme }) => theme.customized.colors.buttonsLinks});
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 0;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const PinInputContainer = styled.div`
  margin: 8px 0 4px;
`;

const StyledPinInput = styled(PinInput)`
  display: flex;
  justify-content: center;
`;

const ErrorMessage = styled.p`
  color: var(--dcm-phone-verification-nudge-error-color, ${({ theme }) => theme.colors.red?.[55] ?? '#E53935'});
  font-size: 13px;
  font-weight: 600;
  margin: 0;
  text-align: center;
`;

const LoaderContainer = styled.div`
  margin: 12px 0;
`;

const ActionsRow = styled.div<{ hasBackLink: boolean }>`
  align-items: center;
  display: flex;
  justify-content: ${({ hasBackLink }) => (hasBackLink ? 'space-between' : 'flex-end')};
  margin-top: 8px;
  width: 100%;
`;

const BackButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: var(--dcm-phone-verification-nudge-back-button-color, ${({ theme }) => theme.colors.grey[40]});
  cursor: pointer;
  font-size: 14px;
  padding: 0;
`;

const ResendButton = styled.button`
  appearance: none;
  background-color: var(--dcm-phone-verification-nudge-resend-button-bg-color, ${({ theme }) => theme.colors.grey[85]});
  border: none;
  border-radius: var(--dcm-phone-verification-nudge-resend-button-border-radius, 100px);
  color: var(--dcm-phone-verification-nudge-resend-button-color, ${({ theme }) => theme.colors.grey[40]});
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 10px 16px;
  text-transform: var(--dcm-phone-verification-nudge-resend-button-text-transform, uppercase);

  &:disabled {
    cursor: not-allowed;
  }

  &:not(:disabled) {
    background-color: var(
      --dct--customized-colors-buttonsLinks,
      ${({ theme }) => theme.customized.colors.buttonsLinks}
    );
    color: var(--dcm-phone-verification-nudge-resend-button-active-color, ${({ theme }) => theme.colors.white});
  }
`;

const InlineResendLink = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: var(--dct--customized-colors-buttonsLinks, ${({ theme }) => theme.customized.colors.buttonsLinks});
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  margin-top: 4px;
  padding: 0;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;
